import { useState, useCallback } from 'react';
import { parsePhoneNumber, isValidPhoneNumber, type ParseError } from 'libphonenumber-js';
import { ValidationResult, PhoneInputData } from '../types/messageForm.types';
import { defaultPhoneCountry } from '../../../utils/businessDefaults';

/**
 * Custom hook for phone number validation and formatting
 */
export const usePhoneValidation = () => {
  const [validationCache, setValidationCache] = useState<Map<string, ValidationResult>>(new Map());

  /**
   * Validate phone number for a specific country
   */
  const validatePhoneNumber = useCallback((phone: string, countryCode: string): ValidationResult => {
    // Check cache first
    const cacheKey = `${phone}-${countryCode}`;
    if (validationCache.has(cacheKey)) {
      return validationCache.get(cacheKey)!;
    }

    const errors: string[] = [];

    // Clean phone number
    const cleaned = phone.replace(/\D/g, '');

    // Basic validation
    if (!cleaned || cleaned.length < 5) {
      errors.push('Phone number is too short');
      const result: ValidationResult = { isValid: false, errors };
      validationCache.set(cacheKey, result);
      return result;
    }

    // Try to parse with country code
    try {
      let parsed;
      if (phone.startsWith('+')) {
        parsed = parsePhoneNumber(phone);
      } else {
        parsed = parsePhoneNumber(cleaned, countryCode as any);
      }

      if (parsed && isValidPhoneNumber(parsed.number)) {
        const normalized = parsed.format('E.164');
        const result: ValidationResult = { 
          isValid: true, 
          errors: [], 
          normalized 
        };
        validationCache.set(cacheKey, result);
        return result;
      } else {
        errors.push('Invalid phone number format');
      }
    } catch (error) {
      errors.push('Invalid phone number format');
    }

    const result: ValidationResult = { isValid: false, errors };
    validationCache.set(cacheKey, result);
    return result;
  }, [validationCache]);

  /**
   * Normalize phone number to E.164 format
   */
  const normalizePhoneNumber = useCallback((phone: string, countryCode: string): string => {
    try {
      let parsed;
      if (phone.startsWith('+')) {
        parsed = parsePhoneNumber(phone);
      } else {
        parsed = parsePhoneNumber(phone, countryCode as any);
      }

      if (parsed && isValidPhoneNumber(parsed.number)) {
        return parsed.format('E.164');
      }
    } catch (error) {
      console.error('Error normalizing phone number:', error);
    }

    // Fallback: ensure starts with +
    if (!phone.startsWith('+')) {
      return `+${phone}`;
    }
    return phone;
  }, []);

  /**
   * Detect country code from phone number
   */
  const detectCountry = useCallback((phone: string): string => {
    try {
      if (phone.startsWith('+')) {
        const parsed = parsePhoneNumber(phone);
        if (parsed && parsed.country) {
          return parsed.country;
        }
      }
    } catch (error) {
      console.error('Error detecting country:', error);
    }
    return defaultPhoneCountry(); // Default to Egypt
  }, []);

  /**
   * Format phone number for display
   */
  const formatPhoneNumber = useCallback((phone: string, countryCode: string): string => {
    try {
      let parsed;
      if (phone.startsWith('+')) {
        parsed = parsePhoneNumber(phone);
      } else {
        parsed = parsePhoneNumber(phone, countryCode as any);
      }

      if (parsed && isValidPhoneNumber(parsed.number)) {
        return parsed.formatInternational();
      }
    } catch (error) {
      console.error('Error formatting phone number:', error);
    }
    return phone;
  }, []);

  /**
   * Extract phone data from PhoneInput component
   */
  const extractPhoneData = useCallback((phoneData: PhoneInputData | null, rawPhone: string): {
    normalized: string;
    dialCode: string;
    countryCode: string;
  } => {
    // A masked display value (e.g. "*******6631", contacts.viewMobileNumber) must
    // never reach the digit-stripping fallback below — `rawPhone.replace(/\D/g, '')`
    // would silently strip the '*' characters too, leaving just the few visible
    // trailing digits, which then got prepended with whatever dial code was
    // selected/defaulted in the UI and saved as if it were a REAL, complete phone
    // number. Confirmed real production corruption: dozens of contacts ended up
    // with a bogus ~6-digit "WhatsApp number" (e.g. dial code "20" + visible "6631"
    // = "206631") linked as a brand new, entirely separate WhatsApp identity,
    // alongside their real number. Reject outright here instead.
    if (rawPhone.includes('*')) {
      return { normalized: '', dialCode: phoneData?.dialCode || '+20', countryCode: phoneData?.countryCode || phoneData?.country || defaultPhoneCountry() };
    }

    if (phoneData && phoneData.formattedNumber) {
      let normalized = phoneData.formattedNumber;
      if (!normalized.startsWith('+')) {
        // If formattedNumber doesn't start with +, add dialCode
        normalized = phoneData.dialCode
          ? `${phoneData.dialCode}${phoneData.nationalNumber || rawPhone.replace(/\D/g, '')}`
          : `+${phoneData.formattedNumber}`;
      }

      return {
        normalized,
        dialCode: phoneData.dialCode || '+20',
        countryCode: phoneData.countryCode || phoneData.country || defaultPhoneCountry()
      };
    }

    // Fallback: try to parse raw phone
    try {
      let phoneToParse = rawPhone;
      // If phone doesn't start with +, try adding it for parsing
      if (!rawPhone.startsWith('+')) {
        phoneToParse = `+${rawPhone.trim()}`;
      }
      
      let parsed;
      try {
        parsed = parsePhoneNumber(phoneToParse);
      } catch (e) {
        // If parsing fails with +, try parsing without it as fallback
        try {
          parsed = parsePhoneNumber(rawPhone);
        } catch (e2) {
          // Continue to next fallback
        }
      }
      
      if (parsed) {
        const normalized = parsed.format('E.164');
        const detected = parsed.country || detectCountry(normalized);
        const dialCode = `+${parsed.countryCallingCode}`;
        
        return {
          normalized,
          dialCode,
          countryCode: detected || defaultPhoneCountry()
        };
      }
      
      // Try with default country
      const normalized = normalizePhoneNumber(rawPhone, defaultPhoneCountry());
      const detected = detectCountry(normalized);
      let dialCode = '+20';
      try {
        const parsedWithDefault = parsePhoneNumber(normalized);
        if (parsedWithDefault) {
          dialCode = `+${parsedWithDefault.countryCallingCode}`;
          return {
            normalized,
            dialCode,
            countryCode: parsedWithDefault.country || detected || defaultPhoneCountry()
          };
        }
      } catch (e) {
        // Keep default
      }
      
      return {
        normalized,
        dialCode,
        countryCode: detected || defaultPhoneCountry()
      };
    } catch (error) {
      // Last resort fallback - try to detect from digits
      const cleaned = rawPhone.replace(/\D/g, '');
      if (cleaned.length >= 3) {
        // Try to detect country from common dial codes
        if (cleaned.startsWith('966')) {
          return {
            normalized: `+${cleaned}`,
            dialCode: '+966',
            countryCode: 'SA'
          };
        } else if (cleaned.startsWith('20')) {
          return {
            normalized: `+${cleaned}`,
            dialCode: '+20',
            countryCode: defaultPhoneCountry()
          };
        } else if (cleaned.startsWith('1') && cleaned.length > 5) {
          return {
            normalized: `+${cleaned}`,
            dialCode: '+1',
            countryCode: 'US'
          };
        }
      }
      
      return {
        normalized: `+${cleaned}`,
        dialCode: '+20',
        countryCode: defaultPhoneCountry()
      };
    }
  }, [normalizePhoneNumber, detectCountry]);

  return {
    validatePhoneNumber,
    normalizePhoneNumber,
    detectCountry,
    formatPhoneNumber,
    extractPhoneData
  };
};

